import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { 
  BarChart3, 
  Users, 
  TrendingUp, 
  Euro, 
  Activity, 
  RefreshCw,
  Loader2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Search,
  Crown,
  Shield,
  Zap,
  Mail,
  GraduationCap,
  Pencil,
  Save
} from "lucide-react";
import { KlartextTrainingAdmin } from "./KlartextTrainingAdmin";

interface AnalyticsData {
  overview: {
    totalChecks: number;
    totalUsers: number;
    paidUsers: number;
    conversionRate: number;
    avgInputLength: number;
    checksToday: number;
  };
  ampelDistribution: Record<string, number>;
  planDistribution: Record<string, number>;
  checksPerDay: Array<{ date: string; count: number }>;
  signupsPerDay: Array<{ date: string; count: number }>;
  revenue: {
    mrr: number;
    totalRevenue: number;
    revenueByPlan: Record<string, number>;
  };
}

interface KlartextUser {
  id: string;
  email: string | null;
  plan: string;
  stripe_customer_id: string | null;
  subscription_end: string | null;
  created_at: string;
  updated_at: string;
}

interface EmailSignup {
  id: string;
  email: string;
  source: string | null;
  created_at: string;
}

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  subtitle,
  trend,
  colorClass = "bg-primary/10 text-primary"
}: { 
  title: string; 
  value: string | number; 
  icon: React.ElementType;
  subtitle?: string;
  trend?: "up" | "down" | "neutral";
  colorClass?: string;
}) => (
  <Card className="bg-card border-border">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-3 rounded-full ${
          trend === "up" ? "bg-green-500/10 text-green-500" :
          trend === "down" ? "bg-red-500/10 text-red-500" :
          colorClass
        }`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AmpelBar = ({ label, count, total, color }: { 
  label: string; 
  count: number; 
  total: number;
  color: "green" | "yellow" | "red";
}) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colorClasses = {
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500"
  };
  const iconMap = {
    green: CheckCircle,
    yellow: AlertTriangle,
    red: XCircle
  };
  const Icon = iconMap[color];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${
            color === "green" ? "text-green-500" :
            color === "yellow" ? "text-yellow-500" :
            "text-red-500"
          }`} />
          <span className="font-medium">{label}</span>
        </div>
        <span className="text-muted-foreground">{count} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const SimpleChart = ({ 
  data, 
  title
}: { 
  data: Array<{ date: string; count: number }>;
  title: string;
}) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const last7Days = data.slice(-7);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-24">
          {last7Days.map((item, index) => (
            <div key={index} className="flex-1 flex flex-col items-center gap-1">
              <div 
                className="w-full bg-cyan-500 rounded-t transition-all duration-300"
                style={{ 
                  height: `${(item.count / maxCount) * 100}%`,
                  minHeight: item.count > 0 ? '4px' : '0'
                }}
              />
              <span className="text-[10px] text-muted-foreground">
                {new Date(item.date).toLocaleDateString('de-DE', { weekday: 'short' })}
              </span>
            </div>
          ))}
        </div>
        {last7Days.length === 0 && (
          <p className="text-center text-muted-foreground py-4 text-sm">Keine Daten</p>
        )}
      </CardContent>
    </Card>
  );
};

export function KlartextCheckAdmin() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [users, setUsers] = useState<KlartextUser[]>([]);
  const [emailSignups, setEmailSignups] = useState<EmailSignup[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [editingUser, setEditingUser] = useState<KlartextUser | null>(null);
  const [editPlan, setEditPlan] = useState("");
  const [editSubscriptionEnd, setEditSubscriptionEnd] = useState("");
  const [savingUser, setSavingUser] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("klartext-analytics", {
        body: { days: parseInt(days) }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setAnalytics(data);
    } catch (err) {
      console.error("Analytics error:", err);
      toast({
        title: "Fehler beim Laden",
        description: err instanceof Error ? err.message : "Analytics konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from("klartext_profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error("Users error:", err);
      toast({
        title: "Fehler",
        description: "Nutzer konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const openEditDialog = (user: KlartextUser) => {
    setEditingUser(user);
    setEditPlan(user.plan);
    setEditSubscriptionEnd(user.subscription_end ? user.subscription_end.split('T')[0] : "");
  };

  const saveUserChanges = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    try {
      const updateData: { plan: string; subscription_end?: string | null } = {
        plan: editPlan
      };
      
      if (editSubscriptionEnd) {
        updateData.subscription_end = new Date(editSubscriptionEnd).toISOString();
      } else {
        updateData.subscription_end = null;
      }

      const { error } = await supabase
        .from("klartext_profiles")
        .update(updateData)
        .eq("id", editingUser.id);

      if (error) throw error;

      toast({
        title: "Gespeichert",
        description: `Nutzer ${editingUser.email} wurde aktualisiert`,
      });

      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Update error:", err);
      toast({
        title: "Fehler",
        description: "Änderungen konnten nicht gespeichert werden",
        variant: "destructive"
      });
    } finally {
      setSavingUser(false);
    }
  };

  const fetchEmailSignups = async () => {
    setLoadingSignups(true);
    try {
      const { data, error } = await supabase
        .from("klartext_email_signups")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEmailSignups(data || []);
    } catch (err) {
      console.error("Email signups error:", err);
      toast({
        title: "Fehler",
        description: "E-Mail-Signups konnten nicht geladen werden",
        variant: "destructive"
      });
    } finally {
      setLoadingSignups(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "signups") {
      fetchEmailSignups();
    }
  }, [activeTab]);

  const totalAmpelChecks = analytics ? 
    Object.values(analytics.ampelDistribution).reduce((a, b) => (a as number) + (b as number), 0) as number : 0;

  const filteredUsers = users.filter(user =>
    (user.email?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
    user.plan.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSignups = emailSignups.filter(signup =>
    signup.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanBadge = (plan: string) => {
    const planConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
      free: { icon: Shield, color: "bg-slate-100 text-slate-700", label: "Free" },
      pro: { icon: Crown, color: "bg-yellow-100 text-yellow-700", label: "Pro" },
      familie: { icon: Users, color: "bg-blue-100 text-blue-700", label: "Familie" },
      firma: { icon: Zap, color: "bg-purple-100 text-purple-700", label: "Firma" }
    };
    
    const config = planConfig[plan] || planConfig.free;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Card>
      <CardHeader className="border-b bg-slate-50">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-xl flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-600" />
            Klartext-Check Admin
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 Tage</SelectItem>
                <SelectItem value="30">30 Tage</SelectItem>
                <SelectItem value="90">90 Tage</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => {
                fetchAnalytics();
                if (activeTab === "users") fetchUsers();
                if (activeTab === "signups") fetchEmailSignups();
              }}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 h-auto p-0">
            <TabsTrigger 
              value="overview" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-600 data-[state=active]:bg-transparent py-3"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Übersicht
            </TabsTrigger>
            <TabsTrigger 
              value="users"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-600 data-[state=active]:bg-transparent py-3"
            >
              <Users className="w-4 h-4 mr-2" />
              Nutzer ({users.length})
            </TabsTrigger>
            <TabsTrigger 
              value="signups"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-600 data-[state=active]:bg-transparent py-3"
            >
              <Mail className="w-4 h-4 mr-2" />
              E-Mail-Signups ({emailSignups.length})
            </TabsTrigger>
            <TabsTrigger 
              value="training"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-600 data-[state=active]:bg-transparent py-3"
            >
              <GraduationCap className="w-4 h-4 mr-2" />
              Training
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="p-6 space-y-6 mt-0">
            {loading && !analytics ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
              </div>
            ) : analytics ? (
              <>
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Gesamt Checks"
                    value={analytics.overview.totalChecks.toLocaleString()}
                    icon={BarChart3}
                    subtitle={`${analytics.overview.checksToday} heute`}
                    colorClass="bg-cyan-500/10 text-cyan-500"
                  />
                  <StatCard
                    title="Registrierte Nutzer"
                    value={analytics.overview.totalUsers.toLocaleString()}
                    icon={Users}
                    subtitle={`${analytics.overview.paidUsers} zahlend`}
                    colorClass="bg-blue-500/10 text-blue-500"
                  />
                  <StatCard
                    title="Conversion Rate"
                    value={`${analytics.overview.conversionRate}%`}
                    icon={TrendingUp}
                    subtitle="Free → Paid"
                    trend={analytics.overview.conversionRate > 5 ? "up" : "neutral"}
                  />
                  <StatCard
                    title="MRR"
                    value={`€${analytics.revenue.mrr.toFixed(2)}`}
                    icon={Euro}
                    subtitle={`€${analytics.revenue.totalRevenue.toFixed(0)} ARR`}
                    trend="up"
                  />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <SimpleChart 
                    data={analytics.checksPerDay} 
                    title="Checks pro Tag"
                  />
                  <SimpleChart 
                    data={analytics.signupsPerDay} 
                    title="Neue Nutzer pro Tag"
                  />
                </div>

                {/* Distribution Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Ampel Distribution */}
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Ampel-Verteilung
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <AmpelBar 
                        label="Grün (Sicher)" 
                        count={analytics.ampelDistribution.green || analytics.ampelDistribution.gruen || 0}
                        total={totalAmpelChecks}
                        color="green"
                      />
                      <AmpelBar 
                        label="Gelb (Verdächtig)" 
                        count={analytics.ampelDistribution.yellow || analytics.ampelDistribution.gelb || 0}
                        total={totalAmpelChecks}
                        color="yellow"
                      />
                      <AmpelBar 
                        label="Rot (Betrug)" 
                        count={analytics.ampelDistribution.red || analytics.ampelDistribution.rot || 0}
                        total={totalAmpelChecks}
                        color="red"
                      />
                    </CardContent>
                  </Card>

                  {/* Plan Distribution */}
                  <Card className="bg-card border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Plan-Verteilung
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(analytics.planDistribution).map(([plan, count]) => {
                          const countNum = count as number;
                          const percentage = analytics.overview.totalUsers > 0 
                            ? (countNum / analytics.overview.totalUsers) * 100 
                            : 0;
                          return (
                            <div key={plan} className="flex items-center justify-between py-1">
                              {getPlanBadge(plan)}
                              <span className="text-sm text-muted-foreground">
                                {countNum} ({percentage.toFixed(1)}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {Object.keys(analytics.revenue.revenueByPlan).length > 0 && (
                        <div className="mt-4 pt-3 border-t border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Revenue nach Plan</p>
                          {Object.entries(analytics.revenue.revenueByPlan).map(([plan, revenue]) => (
                            <div key={plan} className="flex items-center justify-between text-sm py-1">
                              <span className="text-muted-foreground capitalize">{plan}</span>
                              <span className="font-medium">€{(revenue as number).toFixed(2)}/mo</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <p className="text-center py-12 text-muted-foreground">Keine Daten verfügbar</p>
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <div className="p-4 border-b bg-slate-50">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Nutzer suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {loadingUsers ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Keine Nutzer gefunden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Stripe ID</TableHead>
                      <TableHead>Abo endet</TableHead>
                      <TableHead>Registriert</TableHead>
                      <TableHead className="w-20">Aktion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">
                          {user.email || <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>{getPlanBadge(user.plan)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {user.stripe_customer_id || "-"}
                        </TableCell>
                        <TableCell>
                          {user.subscription_end ? formatDate(user.subscription_end) : "-"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(user.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="signups" className="mt-0">
            <div className="p-4 border-b bg-slate-50">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="E-Mail suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {loadingSignups ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
              </div>
            ) : filteredSignups.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Keine E-Mail-Signups gefunden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Quelle</TableHead>
                      <TableHead>Angemeldet am</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSignups.map((signup) => (
                      <TableRow key={signup.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{signup.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{signup.source || "klartext-check"}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(signup.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="training" className="p-6 mt-0">
            <KlartextTrainingAdmin />
          </TabsContent>
        </Tabs>
      </CardContent>

      {/* User Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nutzer bearbeiten</DialogTitle>
            <DialogDescription>
              {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan">Plan</Label>
              <Select value={editPlan} onValueChange={setEditPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Plan auswählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="familie">Familie</SelectItem>
                  <SelectItem value="firma">Firma</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subscription_end">Abo endet am</Label>
              <Input
                id="subscription_end"
                type="date"
                value={editSubscriptionEnd}
                onChange={(e) => setEditSubscriptionEnd(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leer lassen für unbegrenzt (z.B. für Gratis-Accounts)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>
              Abbrechen
            </Button>
            <Button onClick={saveUserChanges} disabled={savingUser}>
              {savingUser ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
